import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../../api/client'
import { useSession } from '../../store/user.store'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { MoonPhaseIcon } from '../../components/luna/MoonPhaseIcon'

export function LunaIndexRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const lang = (user?.language ?? 'ru') as 'ru' | 'en'

  const lunarQuery = useQuery({
    queryKey: ['lunar', 'today'],
    queryFn: () => api.lunarToday(),
    staleTime: 1000 * 60 * 60,
  })

  return (
    <div className="flex flex-col gap-5 pb-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl text-sage-800">{t('tabs.luna')}</h1>
        <p className="text-sm text-sage-600">{t('lunar.index_intro')}</p>
      </header>

      <section className="flex flex-col gap-3 rounded-3xl bg-white/70 p-5 ring-1 ring-sage-200/60">
        <div className="flex items-center gap-3">
          <MoonPhaseIcon
            phase={lunarQuery.data?.phase ?? 'new'}
            className="h-8 w-8 text-sage-700"
          />
          <h2 className="font-serif text-lg text-sage-800">{t('lunar.today_title')}</h2>
        </div>
        {lunarQuery.isLoading && <LoadingDots compact />}
        {lunarQuery.data && (
          <div className="flex flex-col gap-1 text-sage-800">
            <p className="text-xs uppercase tracking-wider text-sage-500">
              {t(`lunar.phase.${lunarQuery.data.phase}` as never)}
            </p>
            <p className="leading-relaxed">{lunarQuery.data.energyHint[lang]}</p>
            <p className="mt-1 text-xs text-sage-500">
              {t('home.lunar_illumination', { percent: Math.round(lunarQuery.data.illumination * 100) })}
            </p>
          </div>
        )}
      </section>

      <ul className="flex flex-col gap-3">
        <li>
          <Link
            to="/luna/month"
            className="flex items-center gap-3 rounded-2xl bg-white/70 p-4 ring-1 ring-sage-200/60 transition hover:bg-white active:scale-[0.99]"
          >
            <img
              src="/icons/luna-calendar.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-10 shrink-0 select-none"
              draggable={false}
            />
            <div className="min-w-0 flex-1">
              <h3 className="font-serif text-lg text-sage-800">{t('lunar.month_title')}</h3>
              <p className="text-sm text-sage-600">{t('lunar.month_desc')}</p>
            </div>
          </Link>
        </li>
        <li>
          <Link
            to="/luna/auspicious"
            className="flex items-center gap-3 rounded-2xl bg-white/70 p-4 ring-1 ring-sage-200/60 transition hover:bg-white active:scale-[0.99]"
          >
            <img
              src="/icons/luna-auspicious.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-10 shrink-0 select-none"
              draggable={false}
            />
            <div className="min-w-0 flex-1">
              <h3 className="font-serif text-lg text-sage-800">{t('lunar.auspicious_title')}</h3>
              <p className="text-sm text-sage-600">{t('lunar.auspicious_desc')}</p>
            </div>
          </Link>
        </li>
        <li>
          <Link
            to="/luna/dreams"
            className="flex items-center gap-3 rounded-2xl bg-white/70 p-4 ring-1 ring-sage-200/60 transition hover:bg-white active:scale-[0.99]"
          >
            <img
              src="/icons/luna-dreams.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-10 shrink-0 select-none"
              draggable={false}
            />
            <div className="min-w-0 flex-1">
              <h3 className="font-serif text-lg text-sage-800">{t('lunar.dreams_title')}</h3>
              <p className="text-sm text-sage-600">{t('lunar.dreams_desc')}</p>
            </div>
          </Link>
        </li>
      </ul>
    </div>
  )
}
